import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";

export const DEAL_DETAILS_SECTION_ID = "deal-fields";
export const PRODUCT_SERVICES_SECTION_ID = "product-services-fields";

const RECURRING_REVENUE_NAMES = ["arr", "mrr"];

// Each recurring-revenue metric is stored twice, once per currency basis: the
// terms half carries an `_local` suffix on its key, which the fields API
// camelCases into `arrLocal`. Step 2 derives both halves from the services, so
// both are dropped from step 1. (Not every money column is paired this way —
// ACV/TCV hold terms values with no twin — which is why the strip below only
// counts when the remainder is a known recurring-revenue name.)
//
// Deliberately `local`, not `_local`: the key is camelCased and lowercased
// before this is matched, so the underscore is already gone.
const TERMS_CURRENCY_SUFFIX = "local";

const normalize = (value: string) => value.trim().toLowerCase();

const stripTermsCurrencySuffix = (key: string) =>
  key.endsWith(TERMS_CURRENCY_SUFFIX)
    ? key.slice(0, -TERMS_CURRENCY_SUFFIX.length)
    : key;

// The key is the stable identity: a label is editable in Field Management and
// is also composed with the currency basis on the server, so matching on it
// alone lets a rename resurface the field as a required step-1 input. The label
// match stays as a fallback for accounts whose ARR field carries a custom key.
export const isRecurringRevenueField = (field: FormFieldNewType): boolean =>
  RECURRING_REVENUE_NAMES.includes(
    stripTermsCurrencySuffix(normalize(field.name))
  ) || RECURRING_REVENUE_NAMES.includes(normalize(field.label ?? ""));

// Splits the creation fields into the two steps, dropping the ARR/MRR field.
export const buildWizardSections = (
  sections: FormSectionProps[],
  stepTwoFieldNames: string[]
): FormSectionProps[] => {
  const dealDetailFields: FormFieldNewType[] = [];
  const productServiceFields: FormFieldNewType[] = [];

  sections.forEach((section) =>
    section.fields?.forEach((field) => {
      if (isRecurringRevenueField(field)) return;
      if (stepTwoFieldNames.includes(field.name)) {
        productServiceFields.push({
          ...field,
          labelPosition: "top",
          layout: "half"
        });
        return;
      }
      dealDetailFields.push(field);
    })
  );

  return [
    { id: DEAL_DETAILS_SECTION_ID, fields: dealDetailFields },
    { id: PRODUCT_SERVICES_SECTION_ID, fields: productServiceFields }
  ];
};
